import { ContactButton } from "@components/inputs/ContactButtonGroup";
import ScrollBox from "@components/layouts/ScrollBox";
import useContactLinks from "@hooks/useContactLinks";
import { ButtonIcon, cn, ContactUrl, Dialog, useCustomEventsListener } from "kz-ui-sdk";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdArrowForward, MdClose } from "react-icons/md";
import { EVENT_CLOSE_CONTACT_LINKS_DIALOG, EVENT_OPEN_CONTACT_LINKS_DIALOG } from "./constants";
import { ContactLinksDialogOption } from "./utils";

export default function ContactLinksDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const refCallback = useRef<() => void>();

  const { contactLinks } = useContactLinks();

  const [customContactLinks, setCustomContactLinks] = useState<ContactUrl[]>([]);

  const links = useMemo(() => {
    return customContactLinks.length ? customContactLinks : contactLinks;
  }, [customContactLinks, contactLinks]);

  useCustomEventsListener({
    [EVENT_CLOSE_CONTACT_LINKS_DIALOG]: () => {
      handleClose();
    },
    [EVENT_OPEN_CONTACT_LINKS_DIALOG]: (event: CustomEvent<ContactLinksDialogOption>) => {
      handleOpen(event.detail || {});
    },
  });

  const handleOpen = useCallback(({ callback, contactLinks }: ContactLinksDialogOption) => {
    refCallback.current = callback;
    setOpen(true);
    setCustomContactLinks(contactLinks || []);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (refCallback.current) {
      refCallback.current();
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      title={t("contact us")}
      classes={{
        "container&": "relative z-[9999] max-h-[calc(100dvh-32px)] flex flex-col",
        "title&": "font-bold text-xl leading-8 align-middle mb-2",
        "containerWrapper&": "!z-[10000]",
        "backDrop&": "!z-[9999]",
      }}
      transition
    >
      <ScrollBox
        classes={{
          root: "flex flex-1 flex-col overflow-y-hidden -mx-4 -mb-4",
          content: "flex flex-grow flex-col overflow-y-auto items-center px-4",
          shadow: "shadow-[0_0_16px_20px_#17114a] animate-fade-in",
        }}
      >
        <div className="mb-6 flex w-full flex-col gap-2 rounded-[6px] border border-stroke-soft bg-grdHigh p-2">
          {links?.map((contact) => (
            <a
              key={contact.id}
              href={contact.url}
              target="_blank"
              rel="noreferrer noopener"
              className={cn(
                "flex min-h-12 items-center gap-3 border-b border-stroke-base pb-2",
                "last:min-h-10 last:border-b-0 last:pb-0",
              )}
            >
              <ContactButton
                image={contact.image}
                description={contact.description}
              />
              <div className="flex-1">
                <span className="hyphens-auto break-words text-sm font-medium">{contact.description}</span>
              </div>

              <ButtonIcon
                variant="ghost"
                className="!size-6 shrink-0"
                icon={<MdArrowForward size={14} />}
              />
            </a>
          ))}
        </div>
      </ScrollBox>

      <ButtonIcon
        className="absolute right-4 top-4 flex !size-8 !bg-transparent"
        icon={
          <MdClose
            width={24}
            height={24}
            onClick={(e) => {
              e.preventDefault();
              handleClose();
            }}
            className="size-6"
          />
        }
      />
    </Dialog>
  );
}
